import { memo, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useCheckOnNegativePriceWhenSimulate,
  useGetBuilderOrderRequestData,
} from 'shared-modules/services/hooks/builder';
import { useComprehensiveEvaluationTargetTermIds } from 'shared-modules/hooks/comprehensiveEvaluation';
import { Button } from '../../../../../components';
import {
  changeBuilderOrderEditStatus,
  getBuilderSimulationDataRequest,
  getSilentMultiBuilderSimulationDataRequest,
  openBuilderAddToPortfolioModal,
} from '../../../../../redux/actions';
import styles from './footer.module.scss';
import { openBuilderAddToCartModal } from '../../../../../redux/actions/modalActions';

export const Footer = memo(() => {
  const dispatch = useDispatch();
  const allowOrderEdit = useSelector((state) => state.builder.allowOrderEdit);
  const logicGroupsList = useSelector((state) => state.builder.logicGroupsList);
  const orderSettingsList = useSelector((state) => state.builder.orderSettingsList);
  const simulationDataIsLoading = useSelector((state) => state.builder.simulationDataIsLoading);
  const { isEditLogicGroup } = useSelector((state) => state.builder.editLogicGroup);
  const getOrderRequestData = useGetBuilderOrderRequestData();

  const checkOnNegativePrice = useCheckOnNegativePriceWhenSimulate();

  const changeEditMode = useCallback(
    () => dispatch(changeBuilderOrderEditStatus({ allowOrderEdit: !allowOrderEdit })),
    [dispatch, allowOrderEdit],
  );

  const defaultSelectionTermId = useSelector((state) => state.constants.defaultSelectionTermId);
  const termIdsForAssessment = useComprehensiveEvaluationTargetTermIds({ excludeTermId: defaultSelectionTermId });

  const getSimulationData = useCallback(() => {
    const successCallback = () => {
      const orderRequestData = getOrderRequestData();

      dispatch(
        getSilentMultiBuilderSimulationDataRequest({
          orderRequestData,
          termIdList: termIdsForAssessment,
        }),
      );
      dispatch(
        getBuilderSimulationDataRequest({ orderRequestData, callback: changeEditMode, termId: defaultSelectionTermId }),
      );
    };

    checkOnNegativePrice(successCallback);
  }, [
    changeEditMode,
    checkOnNegativePrice,
    dispatch,
    getOrderRequestData,
    termIdsForAssessment,
    defaultSelectionTermId,
  ]);

  const onAddToCartClick = useCallback(() => {
    const successCallback = () => {
      dispatch(openBuilderAddToCartModal());
    };
    checkOnNegativePrice(successCallback);
  }, [dispatch, checkOnNegativePrice]);

  const onRunNowClick = useCallback(() => {
    const successCallback = () => {
      dispatch(openBuilderAddToPortfolioModal());
    };
    checkOnNegativePrice(successCallback);
  }, [dispatch, checkOnNegativePrice]);

  const isDisabled = useMemo(
    () => !logicGroupsList.length && !orderSettingsList.length,
    [logicGroupsList, orderSettingsList],
  );

  useEffect(() => {
    if (isDisabled) {
      dispatch(changeBuilderOrderEditStatus({ allowOrderEdit: true }));
    }
  }, [isDisabled, dispatch]);

  return (
    <div className={styles.container}>
      {allowOrderEdit ? (
        <div className={styles.buttonGroup}>
          <Button
            disabled={isDisabled || isEditLogicGroup}
            loading={simulationDataIsLoading}
            tabIndex="0"
            secondary
            width={224}
            onClick={() => {
              getSimulationData();
            }}
          >
            シミュレーションを見る
          </Button>
          <Button
            disabled={isDisabled || isEditLogicGroup}
            width={160}
            onClick={onAddToCartClick}
            tabIndex="0"
            secondary
          >
            カートに追加
          </Button>
          <Button
            className={styles.greenButton}
            disabled={isDisabled || isEditLogicGroup}
            width={160}
            onClick={onRunNowClick}
            tabIndex="0"
          >
            確認画面へ進む
          </Button>
        </div>
      ) : (
        <div className={styles.buttonGroup}>
          <Button
            onClick={() => {
              changeEditMode();
            }}
            tabIndex="0"
            width={224}
            secondary
          >
            プライスチャートに戻る
          </Button>

          <Button disabled={isDisabled} width={160} onClick={onAddToCartClick} tabIndex="0" secondary>
            カートに追加
          </Button>

          <Button className={styles.greenButton} disabled={isDisabled} width={160} onClick={onRunNowClick} tabIndex="0">
            確認画面へ進む
          </Button>
        </div>
      )}
    </div>
  );
});
